/**
 * เสนอคำขอเปลี่ยนแปลงข้อมูลสินค้า — `POST /v1/products/{id}/change-requests`
 *
 * ฟอร์มนี้ส่งออกไปสามคีย์ตาม contract: `change_type`, `plant_code` และ
 * `proposed_changes` สิ่งที่จงใจ "ไม่มี" ที่นี่ และเหตุผล:
 *
 * - **ไม่มีช่อง `baseline`** และไม่มีทางให้ผู้ใช้แตะมันเลย เซิร์ฟเวอร์เป็นผู้เติม
 *   จาก record จริง ณ ตอนสร้างข้อเสนอ ถ้า client เขียน baseline เองได้ การเทียบ
 *   ค่าตอน approve ก็จะเทียบกับความเห็นของผู้เสนอ และ `409 stale_fields` ซึ่งเป็น
 *   หัวใจของ D-042 ทั้งข้อ จะไม่กันอะไรเลย — ค่าปัจจุบันที่แสดงข้างช่องกรอกจึงเป็น
 *   **ข้อมูลอ่านอย่างเดียว** ไม่ใช่ค่าเริ่มต้นที่แก้แล้วส่งไปได้
 * - **ไม่มีช่องกรอกตอนขอปิดใช้งาน** — `DEACTIVATE` ต้องมี `proposed_changes` ว่าง
 *   และ `plant_code` เป็น `null` เสมอ เพราะการปิด product เป็นการตัดสินระดับ record
 *   ไม่ใช่ระดับ Plant (D-042)
 * - **ไม่มีช่องแก้รหัสสินค้า สถานะ หรือฟิลด์ผู้กระทำ** — `proposed_changes` รับ
 *   เฉพาะ field ที่ `PATCH` รับได้ contract ตอบ `400` ให้คำขอที่เอ่ยถึงฟิลด์อื่น
 * - **ไม่มีตัวเลือกโหมดเติมเต็ม** — เฟส 1 เลือกได้ค่าเดียวคือ `PUSH` (D-034)
 *   ช่องที่มีค่าให้เลือกค่าเดียวคือช่องที่ไม่มีอะไรให้เสนอ
 *
 * ช่องว่างแปลว่า "ไม่เสนอแก้ฟิลด์นี้" ไม่ใช่ "เสนอให้ล้างค่า" — การล้างค่าเป็น
 * ความตั้งใจคนละอย่างที่หน้าจอนี้ยังไม่เปิดให้ทำ ดีกว่าเปิดไว้แบบที่ผู้ใช้แยก
 * สองอย่างนี้ไม่ออกแล้วเผลอเสนอให้ลบต้นทุนทิ้ง
 */

import { useState, type FormEvent } from 'react';

import { ProductFailureNotice } from './ProductFailureNotice';
import { STEEL_LABELS, orDash, steelProblems, type SteelTexts } from './productView';
import { InfoHint } from '../components/InfoHint';
import {
  createProductChangeRequest,
  type ProductFailure,
} from '../../lib/products/client';
import {
  PRODUCT_CHANGE_GENERAL_FIELDS,
  PRODUCT_CHANGE_PLANT_FIELDS,
  PRODUCT_CHANGE_TYPES,
  PRODUCT_STEEL_FIELDS,
  type CreateProductChangeRequestBody,
  type Product,
  type ProductChangeGeneralField,
  type ProductChangePlantField,
  type ProductChangeRequest,
  type ProductChangeType,
} from '../../lib/products/types';

export interface ProductChangeRequestFormProps {
  /** record ที่กำลังถูกแสดงอยู่ — ค่าปัจจุบันที่โชว์ข้างช่องกรอกมาจากก้อนนี้ */
  readonly product: Product;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** เซิร์ฟเวอร์รับข้อเสนอแล้ว — ผู้เรียกเป็นผู้ไปดึงรายการล่าสุดมาแสดง */
  readonly onCreated: (changeRequest: ProductChangeRequest) => void;
}

type SubmitState =
  | { readonly status: 'idle' }
  | { readonly status: 'submitting' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | ProductFailure;

/** ค่าที่กรอกได้ — ทุกช่องเป็นข้อความ เพราะ `NUMERIC` ต้องไม่ผ่าน `Number` (D-039/contract) */
type GeneralValues = Record<ProductChangeGeneralField, string>;
type PlantValues = Record<ProductChangePlantField, string>;

const EMPTY_GENERAL: GeneralValues = {
  name: '',
  product_type: '',
  base_uom: '',
  kg_per_piece: '',
  material_spec: '',
  material_form: '',
  thickness_mm: '',
  width_mm: '',
  length_mm: '',
};

const EMPTY_PLANT: PlantValues = {
  valuation_class: '',
  lot_size_procedure: '',
  planned_delivery_time: '',
  safety_stock: '',
  unit_cost: '',
};

const GENERAL_LABELS: Readonly<Record<ProductChangeGeneralField, string>> = {
  name: 'ชื่อสินค้า (name)',
  product_type: 'ประเภทสินค้า (product_type)',
  base_uom: 'หน่วยนับหลัก (base_uom)',
  ...STEEL_LABELS,
};

const PLANT_LABELS: Readonly<Record<ProductChangePlantField, string>> = {
  valuation_class: 'Valuation class',
  lot_size_procedure: 'วิธีกำหนดขนาดล็อต',
  planned_delivery_time: 'Lead time (วัน)',
  safety_stock: 'สต๊อกปลอดภัย (safety_stock)',
  unit_cost: 'ต้นทุนต่อหน่วย (unit_cost)',
};

const CHANGE_TYPE_LABELS: Readonly<Record<ProductChangeType, string>> = {
  FIELD_UPDATE: 'เสนอแก้ไขข้อมูล',
  DEACTIVATE: 'เสนอปิดใช้งานสินค้า (INACTIVE)',
};

/** เลขจำนวนเต็มไม่ติดลบเท่านั้น — `planned_delivery_time` เป็น INT ไม่ใช่ NUMERIC */
const INTEGER_PATTERN = /^\d+$/;

/** ค่าปัจจุบันของฟิลด์ระดับ product — สิ่งที่จะกลายเป็น `baseline` ฝั่งเซิร์ฟเวอร์ */
function currentGeneralValue(product: Product, field: ProductChangeGeneralField): string | null {
  return product[field];
}

/** ช่องที่ใส่ในข้อเสนอ: ตัวเลขเหล็กส่งเป็นข้อความที่พิมพ์ ไม่ผ่าน `Number` (D-165) */
const STEEL_FIELD_SET: ReadonlySet<string> = new Set(PRODUCT_STEEL_FIELDS);

/**
 * ตรวจชุดเหล็กกับค่า**หลังรวม**ข้อเสนอกับของเดิม — ตรงกับที่เซิร์ฟเวอร์ตัดสิน (D-165);
 * ข้อเสนอนี้เสนอ "ตั้งค่า" เท่านั้น ช่องว่างคือไม่แตะ ไม่ใช่ล้าง
 */
function mergedSteelProblems(product: Product, general: GeneralValues): readonly string[] {
  const merged: Record<string, string> = {};
  for (const field of PRODUCT_STEEL_FIELDS) {
    const typed = general[field].trim();
    merged[field] = typed !== '' ? typed : (product[field] ?? '');
  }
  return steelProblems(merged as SteelTexts);
}

type BuildResult =
  | { readonly ok: true; readonly body: CreateProductChangeRequestBody }
  | { readonly ok: false; readonly problems: readonly string[] };

/**
 * ประกอบ body — คีย์ที่ออกไปถูกเขียนไว้ตรงนี้ทีละตัว ไม่ spread state ของฟอร์ม
 *
 * เหตุผลเดียวกับหน้าจอสร้าง/แก้ไข: การ spread คือวิธีที่ฟิลด์ซึ่งเซิร์ฟเวอร์เป็น
 * เจ้าของจะหลุดออกไปกับ request ในวันที่มีคนเพิ่มช่องใหม่
 */
function buildBody(
  product: Product,
  changeType: ProductChangeType,
  plantCode: string | null,
  general: GeneralValues,
  plant: PlantValues,
): BuildResult {
  if (changeType === 'DEACTIVATE') {
    // รูปร่างนี้มาจาก D-042 ตรง ๆ ไม่ใช่ทางลัดของหน้าจอ
    return { ok: true, body: { change_type: 'DEACTIVATE', plant_code: null, proposed_changes: {} } };
  }

  const problems: string[] = [];
  const proposed: Record<string, unknown> = {};

  if (plantCode === null) {
    for (const field of PRODUCT_CHANGE_GENERAL_FIELDS) {
      const typed = general[field].trim();
      if (typed !== '') {
        proposed[field] = typed;
      }
    }
    if (Object.keys(proposed).some((field) => STEEL_FIELD_SET.has(field))) {
      problems.push(...mergedSteelProblems(product, general));
    }
  } else {
    for (const field of PRODUCT_CHANGE_PLANT_FIELDS) {
      const raw = plant[field];
      if (field === 'planned_delivery_time') {
        const typed = raw.trim();
        if (typed === '') {
          continue;
        }
        if (!INTEGER_PATTERN.test(typed)) {
          problems.push('Lead time ต้องเป็นจำนวนเต็มไม่ติดลบ (หน่วยเป็นวัน)');
          continue;
        }
        proposed[field] = Number(typed);
        continue;
      }
      if (field === 'safety_stock' || field === 'unit_cost') {
        // ⚠️ ไม่ trim และไม่แปลงชนิด: ค่าที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
        if (raw !== '') {
          proposed[field] = raw;
        }
        continue;
      }
      const typed = raw.trim();
      if (typed !== '') {
        proposed[field] = typed;
      }
    }
  }

  if (Object.keys(proposed).length === 0) {
    problems.push('ต้องกรอกค่าที่เสนอใหม่อย่างน้อยหนึ่งช่อง');
  }
  if (problems.length > 0) {
    return { ok: false, problems };
  }

  return {
    ok: true,
    body: { change_type: 'FIELD_UPDATE', plant_code: plantCode, proposed_changes: proposed },
  };
}

export function ProductChangeRequestForm({
  product,
  apiBaseUrl,
  fetchImpl,
  onCreated,
}: ProductChangeRequestFormProps) {
  const [changeType, setChangeType] = useState<ProductChangeType>('FIELD_UPDATE');
  /** สตริงว่าง = ระดับ product (`plant_code: null`) ไม่ใช่ "ยังไม่ได้เลือก" */
  const [scope, setScope] = useState('');
  const [general, setGeneral] = useState<GeneralValues>(EMPTY_GENERAL);
  const [plant, setPlant] = useState<PlantValues>(EMPTY_PLANT);
  const [submit, setSubmit] = useState<SubmitState>({ status: 'idle' });

  const selectedPlant =
    scope === '' ? null : (product.plant_data.find((entry) => entry.plant_code === scope) ?? null);
  const plantCode = changeType === 'DEACTIVATE' || selectedPlant === null ? null : scope;

  const onFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submit.status === 'submitting') {
      return;
    }

    const built = buildBody(product, changeType, plantCode, general, plant);
    if (!built.ok) {
      setSubmit({ status: 'invalid-form', problems: built.problems });
      return;
    }

    setSubmit({ status: 'submitting' });
    const result = await createProductChangeRequest(product.product_id, built.body, {
      apiBaseUrl,
      fetchImpl,
    });
    if (result.status !== 'ready') {
      // ความล้มเหลวไม่ลบสิ่งที่กรอกไว้ และไม่แตะรายการข้อเสนอที่แสดงอยู่
      setSubmit(result);
      return;
    }

    setSubmit({ status: 'idle' });
    setGeneral(EMPTY_GENERAL);
    setPlant(EMPTY_PLANT);
    onCreated(result.changeRequest);
  };

  return (
    <section className="cap-card cap-card--form" aria-label="เสนอคำขอเปลี่ยนแปลง">
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          เสนอคำขอเปลี่ยนแปลง
          <InfoHint
            label="เกี่ยวกับการเสนอคำขอเปลี่ยนแปลง"
            text="รายการที่ใช้งานอยู่แก้โดยตรงไม่ได้ (D-011) — ข้อเสนอนี้ต้องผ่านการอนุมัติจากคนอื่น และค่าปัจจุบันจะถูกเซิร์ฟเวอร์บันทึกเป็น baseline ให้เอง"
          />
        </h2>
      </div>

      {submit.status === 'invalid-form' ? (
        <div className="cap-banner cap-banner--warning" role="status" data-testid="product-cr-invalid">
          <strong>ยังส่งไม่ได้ — ข้อมูลไม่ครบ</strong>
          <span>{submit.problems.join(' · ')}</span>
        </div>
      ) : null}

      {submit.status === 'idle' ||
      submit.status === 'submitting' ||
      submit.status === 'invalid-form' ? null : (
        <ProductFailureNotice failure={submit} context="change-request" />
      )}

      <form
        className="cap-form"
        data-testid="product-cr-form"
        onSubmit={(event) => void onFormSubmit(event)}
        noValidate
      >
        <fieldset className="cap-field">
          <legend className="cap-field__label">
            ชนิดของคำขอ (D-042){' '}
            <InfoHint
              label="เกี่ยวกับคำขอชนิดปิดใช้งาน"
              text={
                <span data-testid="product-cr-deactivate-note">
                  การปิดใช้งานไม่มี endpoint แยก และไม่มีช่องให้กรอก — ส่งเป็นคำขอชนิด DEACTIVATE
                  ที่ไม่มีการแก้ฟิลด์ใดเลย และเป็นการตัดสินระดับสินค้า ไม่ใช่ระดับโรงงาน
                </span>
              }
            />
          </legend>
          {PRODUCT_CHANGE_TYPES.map((type) => (
            <label key={type}>
              <input
                type="radio"
                name="change_type"
                value={type}
                data-testid={`product-cr-type-${type}`}
                checked={changeType === type}
                onChange={() => {
                  setChangeType(type);
                  setSubmit({ status: 'idle' });
                }}
              />
              <span>{CHANGE_TYPE_LABELS[type]}</span>
            </label>
          ))}
        </fieldset>

        {changeType === 'DEACTIVATE' ? null : (
          <label className="cap-field">
            <span className="cap-field__label">ขอบเขตของคำขอ</span>
            <select
              className="cap-input"
              name="plant_code"
              data-testid="product-cr-scope"
              value={scope}
              onChange={(event) => {
                setScope(event.target.value);
                setSubmit({ status: 'idle' });
              }}
            >
              <option value="">ระดับสินค้า (ทุกโรงงาน)</option>
              {product.plant_data.map((entry) => (
                <option key={entry.plant_id} value={entry.plant_code}>
                  โรงงาน {entry.plant_code}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับขอบเขตของคำขอเปลี่ยนแปลง"
              text={
                <span data-testid="product-cr-scope-note">
                  เลือกได้เฉพาะโรงงานที่คุณมีสิทธิ์เห็น (D-004) — ข้อเสนอระดับสินค้าส่ง{' '}
                  <code className="cap-table__code">plant_code: null</code> ไปตาม contract
                </span>
              }
            />
          </label>
        )}

        {changeType === 'DEACTIVATE' ? null : (
          <div className="cap-form__grid">
            {plantCode === null
              ? PRODUCT_CHANGE_GENERAL_FIELDS.map((field) => (
                  <label className="cap-field" key={field}>
                    <span className="cap-field__label">{GENERAL_LABELS[field]}</span>
                    {field === 'material_form' ? (
                      <select
                        className="cap-input"
                        name={field}
                        data-testid="product-cr-input-material_form"
                        value={general[field]}
                        onChange={(event) =>
                          setGeneral((current) => ({ ...current, [field]: event.target.value }))
                        }
                      >
                        <option value="">— ไม่เสนอแก้ —</option>
                        <option value="SHEET">SHEET</option>
                        <option value="COIL">COIL</option>
                      </select>
                    ) : (
                      <input
                        className="cap-input"
                        name={field}
                        inputMode={
                          field === 'kg_per_piece' ||
                          field === 'thickness_mm' ||
                          field === 'width_mm' ||
                          field === 'length_mm'
                            ? 'decimal'
                            : undefined
                        }
                        value={general[field]}
                        onChange={(event) =>
                          setGeneral((current) => ({ ...current, [field]: event.target.value }))
                        }
                      />
                    )}
                    <span className="cap-field__hint" data-testid={`product-cr-current-${field}`}>
                      ค่าปัจจุบัน: {orDash(currentGeneralValue(product, field))} — ปล่อยว่างไว้
                      ถ้าไม่ได้เสนอแก้ฟิลด์นี้
                    </span>
                  </label>
                ))
              : PRODUCT_CHANGE_PLANT_FIELDS.map((field) => (
                  <label className="cap-field" key={field}>
                    <span className="cap-field__label">{PLANT_LABELS[field]}</span>
                    <input
                      className="cap-input"
                      name={field}
                      inputMode={
                        field === 'safety_stock' || field === 'unit_cost'
                          ? 'decimal'
                          : field === 'planned_delivery_time'
                            ? 'numeric'
                            : undefined
                      }
                      value={plant[field]}
                      onChange={(event) =>
                        setPlant((current) => ({ ...current, [field]: event.target.value }))
                      }
                    />
                    <span className="cap-field__hint" data-testid={`product-cr-current-${field}`}>
                      ค่าปัจจุบัน: {orDash(selectedPlant?.[field] ?? null)} — ปล่อยว่างไว้
                      ถ้าไม่ได้เสนอแก้ฟิลด์นี้
                    </span>
                  </label>
                ))}
          </div>
        )}

        <div className="cap-form__actions">
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="product-cr-submit"
            disabled={submit.status === 'submitting'}
          >
            {submit.status === 'submitting' ? 'กำลังส่งคำขอ…' : 'ส่งคำขอเปลี่ยนแปลง'}
          </button>
          <InfoHint
            label="เกี่ยวกับฟิลด์ที่เซิร์ฟเวอร์เติมให้"
            text="ค่าปัจจุบัน (baseline) ผู้ยื่นคำขอ และสถานะ ถูกเติมโดยเซิร์ฟเวอร์ — ฟอร์มนี้ไม่มีช่องให้ระบุและไม่ส่งค่าเหล่านั้น"
          />
        </div>
      </form>
    </section>
  );
}
